import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import initBootstrapComponents from '../../utils/bootstrap';
import '@splidejs/react-splide/css';
import 'bootstrap/dist/css/bootstrap.min.css';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { useBasket } from '../../context/BasketContext';
import axios from "axios";
import { Riple } from "react-loading-indicators";
import { API_BASE_URL } from '../../constants/api';
import scooterIcon from '../../assets/img/icons/scooter.png';
import wheelchairIcon from '../../assets/img/icons/wheelchair.png';

const ScooterCheckout = () => {

    const navigate = useNavigate();
    const { basket, addToBasket, clearBasket } = useBasket();
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const customerDetails = JSON.parse(localStorage.getItem('customerDetails'));

    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [marketingOptIn, setMarketingOptIn] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        initBootstrapComponents();
    }, []);

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-GB', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const formatTime = (date) => {
        if (!date) return '';
        return new Date(date).toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const typeLabel = (type) => {
        if (!type) return 'Scooter';
        return type.charAt(0).toUpperCase() + type.slice(1);
    };

    const typeIcon = (type) => (type === 'wheelchair' ? wheelchairIcon : scooterIcon);

    const handleRemoveSlot = async (bookingId) => {
        if (bookingId) {
            const formData = new FormData();
            formData.append('bookings[]', bookingId);
            formData.append('key', '7678356c642c91a255471d162205e0f3');

            const token = localStorage.getItem('ambassador_token');
            const headers = {
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'Content-Type': 'multipart/form-data'
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            try {
                await axios.post(`${API_BASE_URL}/api/events/bookings/cancel`, formData, {
                    headers,
                    withCredentials: true
                });
            } catch (error) {
                console.error('Error cancelling booking:', error);
            }
        }

        const updatedSlots = (basket?.slots || []).filter(slot => slot.booking_id !== bookingId);

        if (updatedSlots.length === 0) {
            clearBasket();
            navigate('/scooter-event-select');
        } else {
            addToBasket({
                ...basket,
                slots: updatedSlots
            });
        }
    };

    const handlePlaceOrder = async () => {
        setIsLoading(true);
        setErrorMessage("");
        try {
            const storedDetails = JSON.parse(localStorage.getItem('customerDetails'));

            const formDataObj = new FormData();

            basket.slots.forEach(slot => {
                formDataObj.append('bookings[]', Number(slot.booking_id));
            });

            formDataObj.append('customer[email]', storedDetails.email);
            formDataObj.append('customer[title]', storedDetails.title);
            formDataObj.append('customer[first_name]', `${storedDetails.firstName}`);
            formDataObj.append('customer[last_name]', storedDetails.lastName);
            formDataObj.append('customer[telephone]', storedDetails.telephone);
            const ambassadorId = localStorage.getItem('ambassador_id');
            if (ambassadorId) {
                formDataObj.append('ambassador_id', ambassadorId);
            }
            formDataObj.append('customer[is_existing_motability_scheme_customer]', storedDetails.isExistingCustomer);
            formDataObj.append('customer[marketing_consent]', marketingOptIn ? 'yes' : 'no');
            formDataObj.append('key', '7678356c642c91a255471d162205e0f3');

            const token = localStorage.getItem('ambassador_token');
            const headers = {
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'Content-Type': 'multipart/form-data'
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const resp = await axios.post(`${API_BASE_URL}/api/events/bookings/finalise`, formDataObj, {
                headers,
                withCredentials: true
            });

            const failedMessage = resp.data.data?.failedMessage;
            if (failedMessage) {
                setErrorMessage(failedMessage);
                return;
            }

            localStorage.setItem('bookingConfirmation', JSON.stringify(resp.data.data));

            // Capture the details needed for the scooter/wheelchair confirmation before the basket is cleared
            const scooterBookingConfirmation = {
                event_name: basket.event_name,
                event_start_at: basket.event_start_at,
                event_end_at: basket.event_end_at,
                bookings: basket.slots.map(slot => ({
                    booking_id: slot.booking_id,
                    vehicle_type: slot.vehicle_type,
                    vehicle_name: slot.vehicle?.name,
                    booking_date: slot.start_at,
                    eta_at: slot.eta_at,
                    participant_name: slot.participant_name || `${storedDetails?.firstName} ${storedDetails?.lastName}`.trim()
                }))
            };
            localStorage.setItem('scooterBookingConfirmation', JSON.stringify(scooterBookingConfirmation));

            clearBasket();

            navigate('/scooter-booking-confirmation');
        } catch (error) {
            console.error('Booking error:', error);

            if (error.response?.data?.status === "There is already a registered user with the same email but different type. Please choose a different email.") {
                setErrorMessage("This email address is already registered with a different account type. Please use a different email address.");
            } else if (error.response?.data?.errors) {
                setErrorMessage("Invalid Booking");
            } else {
                setErrorMessage("An error occurred while placing your order. Please try again.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const isBasketEmpty = !basket?.slots || basket.slots.length === 0;

    return (
        <div className="d-flex flex-column justify-content-between min-vh-100" id="body-container">
            <Header />

            <main>
                <section className="bg-motability-mid-black-5 py-32 pt-lg-96 pb-lg-411" id="scooter-checkout">
                    <div className="container">
                        <h1 className="text-primary mb-4 mb-lg-5">
                            Checkout
                        </h1>

                        {errorMessage && (
                            <div className="alert alert-danger mb-4" role="alert">
                                {errorMessage}
                            </div>
                        )}

                        <div className="row row-gap-4 row-gap-lg-0 gx-xxl-5">
                            <div className="col-lg-4">
                                <div className="bg-motability-mid-black-5 border border-motability-mid-black-2 rounded-4 p-4 p-lg-32 h-100">
                                    <h5 className="text-primary mb-3">
                                        Guest details
                                    </h5>

                                    <p className="fw-semibold mb-1">
                                        {customerDetails?.firstName} {customerDetails?.lastName}
                                    </p>
                                    <p className="mb-1">
                                        {customerDetails?.telephone}
                                    </p>
                                    <p className="mb-0">
                                        {customerDetails?.email}
                                    </p>
                                </div>
                            </div>

                            <div className="col-lg-8">
                                <div className="bg-motability-white border border-motability-light-blue rounded-4 p-4 p-lg-32">
                                    <h5 className="text-primary mb-3">
                                        Your order
                                    </h5>

                                    {isBasketEmpty ? (
                                        <p className="mb-0">Your basket is empty.</p>
                                    ) : (
                                        <>
                                            <div className="order-items">
                                                {basket.slots.map((slot) => (
                                                    <div key={slot.id} className="order-item d-flex align-items-start gap-3 py-3 border-bottom border-motability-mid-black-25">
                                                        <div
                                                            className="bg-primary rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                                                            style={{ width: '90px', height: '90px' }}
                                                        >
                                                            <img
                                                                src={typeIcon(slot.vehicle_type)}
                                                                alt=""
                                                                aria-hidden="true"
                                                                style={{ width: '60%', height: 'auto' }}
                                                            />
                                                        </div>

                                                        <div className="d-flex flex-column flex-grow-1">
                                                            <p className="mb-0">
                                                                <span className="fw-semibold">Booking:</span> {typeLabel(slot.vehicle_type)}
                                                            </p>
                                                            <p className="fw-semibold mb-0">
                                                                {basket.event_name}
                                                            </p>
                                                            <p className="mb-0">
                                                                <span className="fw-semibold">Booked From:</span> {formatDate(slot.start_at)}
                                                            </p>
                                                            {slot.eta_at && (
                                                                <p className="mb-0">
                                                                    <span className="fw-semibold">Estimated arrival time:</span> {formatTime(slot.eta_at)}
                                                                </p>
                                                            )}
                                                            <p className="mb-0">
                                                                <span className="fw-semibold">Participant Name:</span> {slot.participant_name || `${customerDetails?.firstName} ${customerDetails?.lastName}`}
                                                            </p>
                                                        </div>

                                                        <button
                                                            type="button"
                                                            className="btn p-0 border-0 bg-transparent flex-shrink-0"
                                                            title="Remove this booking"
                                                            aria-label="Remove this booking"
                                                            onClick={() => handleRemoveSlot(slot.booking_id)}
                                                        >
                                                            <span
                                                                className="d-inline-flex align-items-center justify-content-center rounded-circle bg-danger text-white"
                                                                style={{ width: '28px', height: '28px', fontSize: '1rem', lineHeight: 1 }}
                                                            >
                                                                &times;
                                                            </span>
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="bg-motability-mid-black-5 rounded-4 p-4 my-4">
                                                <p className="mb-3">
                                                    Your personal data will be used to support your experience throughout this website, to manage access to your account, and for other purposes described in our <a href="https://www.motability.co.uk/utilities/privacy-notice" target="_blank" rel="noopener noreferrer" className="fw-semibold text-primary">privacy policy</a>.
                                                </p>

                                                <div className="form-check d-flex align-items-center mb-0">
                                                    <input
                                                        id="agree-terms"
                                                        type="checkbox"
                                                        className="form-check-input cursor-pointer mt-0"
                                                        checked={agreedToTerms}
                                                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                                                    />
                                                    <label htmlFor="agree-terms" className="form-check-label cursor-pointer ps-12">
                                                        I have read and agree to the website terms and conditions <span className="text-danger">*</span>
                                                    </label>
                                                </div>
                                            </div>

                                            <div className="bg-motability-light-orange rounded-4 p-4 my-4">
                                                <div className="form-check d-flex align-items-center mb-0">
                                                    <input
                                                        id="agree-marketing"
                                                        type="checkbox"
                                                        className="form-check-input cursor-pointer mt-0"
                                                        checked={marketingOptIn}
                                                        onChange={(e) => setMarketingOptIn(e.target.checked)}
                                                    />
                                                    <label htmlFor="agree-marketing" className="form-check-label cursor-pointer ps-12">
                                                        I'd like to receive marketing emails about events and updates from the Motability Scheme
                                                    </label>
                                                </div>
                                            </div>

                                            <button
                                                type="button"
                                                className="btn btn-primary rounded-pill px-29 d-flex align-items-center justify-content-center gap-2"
                                                onClick={handlePlaceOrder}
                                                disabled={isLoading || !agreedToTerms}
                                            >
                                                {isLoading ? (
                                                    <>
                                                        <Riple color="#FFFFFF" size="small" />
                                                        <span>Processing...</span>
                                                    </>
                                                ) : (
                                                    'Place order'
                                                )}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
};

export default ScooterCheckout;
